import { useState, useEffect, useMemo } from 'react';
import { validateEntitlements } from '../../../lib/helpers';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { NotificationErrors } from '../../../constants';

import {
    useDrBackupEntitlementsQuery,
    useDrAppliancesQuery,
    useDrPoliciesQuery,
    useLazyProtectionStatusQuery,
} from '../../../api';

export const useDisasterRecoveryData = ({
    userOrg,
    hideDrBlock,
    setHideDrBlock,
    setHideBackupBlock,
    hideMigrationsBlock,
    setHideMigrationsBlock,
}) => {
    const { notifyError } = useNotificationContext();

    // Disaster Recovery State
    const [policiesCount, setPoliciesCount] = useState(0);
    const [scheduledReplications, setScheduledReplications] = useState(0);
    const [protectedVms, setProtectedVms] = useState(0);
    const [replicationsInProgress, setReplicationsInProgress] = useState(0);
    const [recoveryAppliances, setRecoveryAppliances] = useState(0);

    // Backup DR Entitlements State
    const [usedBackupDrEntitlements, setUsedBackupDrEntitlements] = useState(0);
    const [totalBackupDrEntitlements, setTotalBackupDrEntitlements] = useState(0);
    const [availableBackupDrEntitlements, setAvailableBackupDrEntitlements] = useState(0);

    // Query Hooks
    const { data: drPoliciesData, isLoading: drPoliciesDataIsLoading } = useDrPoliciesQuery(
        { userOrg },
        { skip: userOrg === null },
    );
    const { data: drBackupEntitlementsData, isLoading: drBackupEntitlementsDataIsLoading } =
        useDrBackupEntitlementsQuery({ userOrg }, { skip: userOrg === null });
    const { data: drAppliancesData, isLoading: drAppliancesDataIsLoading } = useDrAppliancesQuery(
        { userOrg },
        { skip: userOrg === null },
    );
    const [triggerGetProtectionStatus] = useLazyProtectionStatusQuery();

    const dataIsLoading = drPoliciesDataIsLoading || drBackupEntitlementsDataIsLoading || drAppliancesDataIsLoading;

    useEffect(() => {
        if (!dataIsLoading && userOrg) {
            getAllData();
        }
    }, [userOrg, dataIsLoading]);

    const getAllData = async () => {
        await Promise.all([getDRPolicies(), getDrBackupEntitlements(), getDRAppliances()]);
    };

    const disasterRecoveryData = useMemo(
        () => ({
            title: 'Disaster Recovery',
            chartLabels: { completed: 'Completed Recoveries', available: 'Available Recoveries' },
            hideBlock: hideDrBlock,
            used: usedBackupDrEntitlements,
            available: availableBackupDrEntitlements,
            total: totalBackupDrEntitlements,
            stats: [
                { title: 'Replications in Progress', link: '/recovery_dr_policies', value: replicationsInProgress },
                { title: 'Scheduled Replications', link: '/recovery_dr_policies', value: scheduledReplications },
                { title: 'Protected VMs', link: '/recovery_dr_policies', value: protectedVms },
                { title: 'Recovery Appliances', link: '/recovery_appliance_setup', value: recoveryAppliances },
                {
                    title: 'Entitlement Status',
                    link: '/projects',
                    value: `${availableBackupDrEntitlements}/${totalBackupDrEntitlements}`,
                },
                { title: 'DR Policies', link: '/recovery_dr_policies', value: policiesCount },
            ],
        }),
        [
            hideDrBlock,
            usedBackupDrEntitlements,
            availableBackupDrEntitlements,
            totalBackupDrEntitlements,
            replicationsInProgress,
            scheduledReplications,
            protectedVms,
            recoveryAppliances,
            policiesCount,
        ],
    );

    // Get a list of DR policies and scheduled replications
    const getDRPolicies = () => {
        try {
            let policies = drPoliciesData?.content;
            setPoliciesCount(policies.length);

            let enabledPolicies = policies.filter((policy) => policy.is_active);
            let scheduledReplications = enabledPolicies.reduce((accumulator, policy) => {
                return accumulator + policy.source_ids.length;
            }, 0);
            setScheduledReplications(scheduledReplications);

            getPolicyProtectionStatus(
                policies,
                setProtectedVms,
                setReplicationsInProgress,
                NotificationErrors.protectionStatus,
            );
        } catch (error) {
            notifyError({ description: NotificationErrors.drPolicies });
        }
    };

    const getDrBackupEntitlements = () => {
        try {
            let entitlementBlocks = drBackupEntitlementsData?.content;
            let total;
            if (entitlementBlocks && entitlementBlocks.length > 0) {
                total = entitlementBlocks.reduce((accumulator, ent) => {
                    return accumulator + ent.max_protected_source_count;
                }, 0);

                let used = entitlementBlocks.reduce((accumulator, ent) => {
                    return accumulator + ent.protected_source_count;
                }, 0);

                let entitlementsAreValid = validateEntitlements(entitlementBlocks);
                let available = total - used;

                if (!entitlementsAreValid) {
                    setTotalBackupDrEntitlements(0);
                    setAvailableBackupDrEntitlements(0);
                }
                setUsedBackupDrEntitlements(used);
                setTotalBackupDrEntitlements(total);
                setAvailableBackupDrEntitlements(available);
            } else {
                if (hideMigrationsBlock) {
                    setHideMigrationsBlock(false);
                }
                setHideBackupBlock(true);
                setHideDrBlock(true);
            }
        } catch (error) {
            notifyError({ description: NotificationErrors.drEntitlements });
        }
    };

    // Get a list of DR appliances and the total number
    const getDRAppliances = () => {
        try {
            let cloudAccounts = drAppliancesData?.count;
            setRecoveryAppliances(cloudAccounts);
        } catch (error) {
            notifyError({ description: NotificationErrors.protectionAppliannces });
        }
    };

    const getPolicyProtectionStatus = (policies, firstCardState, secondCardState, notificationError) => {
        // Get protection status for each policy
        let protectedVms = 0;
        let backupsInProgress = 0;
        if (policies.length > 0) {
            Promise.all(
                policies.map(async (policy) => {
                    try {
                        let { data } = await triggerGetProtectionStatus(
                            { policyId: policy.id },
                            { preferCachedValue: true },
                        );
                        let vms = data.content;
                        let protectedVirtualMachines = vms.filter((vm) => vm.last_successful_backup_id).length;
                        let inProgress = vms.filter((vm) => vm.running_backup_id).length;

                        return {
                            protectedVirtualMachines,
                            inProgress,
                        };
                    } catch (error) {
                        notifyError({ description: notificationError });
                        return { protectedVirtualMachines: 0, inProgress: 0 };
                    }
                }),
            ).then((results) => {
                protectedVms = results.reduce((sum, r) => sum + r.protectedVirtualMachines, 0);
                backupsInProgress = results.reduce((sum, r) => sum + r.inProgress, 0);

                firstCardState(protectedVms);
                secondCardState(backupsInProgress);
            });
        } else {
            firstCardState(0);
            secondCardState(0);
        }
    };

    return { disasterRecoveryData: dataIsLoading ? null : disasterRecoveryData };
};
